/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";

import AppLayout from "layout/AppLayout";
import axios from "axiosClient";
import SelectInput from "components/common/SelectInput";
import ConfirmModal from "components/ConfirmModal";
import { useUser } from "auth/useUser";
import {
  BridgeError,
  ackJournalEntry,
  bridgeStatus,
  clearStoredBridgeSettings,
  getBridgeSettings,
  loadBridgeSettings,
  reconnectTerminal,
  storeBridgeSettings,
  printReceipt,
  sendCommand,
  setTerminalAddress,
} from "utils/posBridge";
import { buildPrintPayload } from "utils/posReceiptPrint";
import { explainTerminalError } from "./terminalErrors";
import {
  METHOD_ICONS,
  OTHER_TERMINAL_CARDS,
  PAYMENT_TYPE_OPTIONS,
  SALE_TYPE_OPTIONS,
  money,
  moneyUzs,
  toTerminalTender,
} from "./shared";
import "./pos.css";

// The terminal's registered operator. Shift login expects the name exactly as
// it appears on the terminal, so it is prefilled rather than retyped each day.
const DEFAULT_CASHIER = "EDU ACTION MCHJ";

// A completed sale prints twice: one copy for the candidate, one for the file.
// Reprints from the journal stay at a single copy.
const RECEIPT_COPIES = 2;

/** Turns digits into DD/MM/YYYY as the cashier types. */
export const maskExamDate = (value) => {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)];
  return parts.filter(Boolean).join("/");
};

// Paper and fiscal-day commands, each confirmed before it is sent: an X-report
// and a reprint use paper, and a closed Z-report cannot be reopened.
const REPORT_COMMANDS = {
  PrintXReport: {
    title: "Print the X-report?",
    body: "Prints the running totals for the current fiscal day. Nothing is closed.",
    done: "X-report printed",
    confirmLabel: "Print X-report",
  },
  PrintLastReceipt: {
    title: "Reprint the last receipt?",
    body: "The terminal prints its last fiscal receipt again.",
    done: "Receipt reprinted",
    confirmLabel: "Reprint receipt",
  },
  CloseZReport: {
    title: "Close the Z-report?",
    body: "This ends the fiscal day on the terminal and cannot be undone.",
    done: "Z-report closed",
    confirmLabel: "Close Z-report",
    danger: true,
  },
};

const SETTINGS_SCOPES = [
  { value: "user", label: "My account" },
  { value: "system", label: "Every POS user (default)" },
];

const emptySale = {
  exam: "",
  module: "",
  saleType: "Sale",
  firstName: "",
  lastName: "",
  passportId: "",
  pinpp: "",
  email: "",
  phoneNumber: "",
  examDate: "",
  enquiryId: "",
  promocode: "",
  // our own receipt prints on the till's printer, so the terminal's is opt-in
  shouldPrintReceipt: false,
};

// A payment row starts with no method: the cashier has to say how the money
// was taken, rather than a sale defaulting to Cash because nobody changed it.
// `autoAmount` marks an amount the till filled in from the price list, so a
// module change can update it without overwriting what the cashier typed.
const newPayment = (amountUzs = "") => ({
  amountUzs: amountUzs ? String(amountUzs) : "",
  paymentType: "",
  autoAmount: Boolean(amountUzs),
});

const Pill = ({ state, label, value }) => (
  <span className={`pos-pill is-${state}`}>
    <span className="dot" />
    <span className="label">{label}</span>
    <span className="value">{value}</span>
  </span>
);

// Same words and colours as the Promocode Requests page.
const PROMO_REQUEST_META = {
  pending: { color: "warning", label: "Waiting" },
  approved: { color: "success", label: "Approved" },
  rejected: { color: "danger", label: "Rejected" },
};

const PromoRequestBadge = ({ status }) => {
  const meta = PROMO_REQUEST_META[status];
  if (!meta) return <span>{status}</span>;
  return (
    <Badge color={meta.color} className="text-white">
      {meta.label}
    </Badge>
  );
};

const Step = ({ n, title, hint }) => (
  <div className="pos-step">
    {n && <span className="n">{n}</span>}
    <h6>{title}</h6>
    {hint && <span className="hint">{hint}</span>}
  </div>
);

const PosTerminal = () => {
  const user = useUser();
  const isAdmin = user?.role === "Admin" || user?.isSuperadmin;
  // the price list is the till's catalogue - only admins get the shortcut
  const canManagePrices = isAdmin;

  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Asking for a discount code for the candidate being served. The code comes
  // back immediately but is worth nothing until it is approved in Telegram.
  const [promoNote, setPromoNote] = useState("");
  const [promoDiscountType, setPromoDiscountType] = useState("percentage");
  const [promoDiscountValue, setPromoDiscountValue] = useState("");
  const [promoRequesting, setPromoRequesting] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [promoRequests, setPromoRequests] = useState([]);

  const [settings, setSettings] = useState(getBridgeSettings());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsScope, setSettingsScope] = useState("user");
  const [savingSettings, setSavingSettings] = useState(false);
  const [bridgeInfo, setBridgeInfo] = useState(null);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [confirmCommand, setConfirmCommand] = useState(null);
  const [terminalAddress, setAddress] = useState({ host: "", port: "" });

  const [shift, setShift] = useState({
    login: DEFAULT_CASHIER,
    password: "",
  });
  const [openShiftUser, setOpenShiftUser] = useState(
    () => sessionStorage.getItem("posShiftUser") || "",
  );

  const [meta, setMeta] = useState({ products: [] });
  const [sale, setSale] = useState(emptySale);
  const [payments, setPayments] = useState([newPayment()]);
  const [lastResult, setLastResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [unresolved, setUnresolved] = useState([]);
  const [discountOpen, setDiscountOpen] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const data = await bridgeStatus();
      setStatus(data);
      setStatusError(null);
      setAddress((prev) =>
        prev.host || !data.terminal?.host
          ? prev
          : { host: data.terminal.host, port: data.terminal.port },
      );
    } catch (error) {
      setStatus(null);
      setStatusError(error.message);
    }
  }, []);

  const loadUnresolved = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/pos/sale/unresolved");
      setUnresolved(data.items || []);
    } catch {
      /* a convenience list; never block the till on it */
    }
  }, []);

  useEffect(() => {
    // The server holds the bridge settings, so a fresh browser or a restarted
    // laptop reconnects without anyone re-entering the token.
    loadBridgeSettings({ adoptLocal: true }).then((data) => {
      if (data) setBridgeInfo(data);
      setSettings(getBridgeSettings());
      refreshStatus();
    });
    loadUnresolved();
    axios
      .get("/api/pos/meta")
      .then(({ data }) => setMeta(data))
      .catch(() => toast.error("Could not load the POS catalogue"));

    const timer = setInterval(refreshStatus, 10000);
    return () => clearInterval(timer);
  }, [refreshStatus, loadUnresolved]);

  // ------------------------------------------------------------- derived state

  const connected = Boolean(status?.terminal?.connected);
  const hasShift = Boolean(openShiftUser);
  const ready = connected && hasShift;

  const examOptions = useMemo(
    () => [...new Set(meta.products.map((p) => p.exam))],
    [meta.products],
  );
  const moduleOptions = useMemo(
    () =>
      meta.products.filter((p) => p.exam === sale.exam).map((p) => p.module),
    [meta.products, sale.exam],
  );

  // The price list sets the price; the till cannot change it.
  const listPrice = useMemo(() => {
    const row = meta.products.find(
      (p) => p.exam === sale.exam && p.module === sale.module,
    );
    return row ? row.priceAmount : null;
  }, [meta.products, sale.exam, sale.module]);

  // A single payment is almost always the full price, so fill it in - and
  // keep following the price while the cashier hasn't typed their own amount.
  useEffect(() => {
    setPayments((prev) => {
      if (prev.length !== 1) return prev;
      const [only] = prev;
      if (only.amountUzs && !only.autoAmount) return prev;
      const next = listPrice ? String(listPrice) : "";
      if (only.amountUzs === next) return prev;
      return [{ ...only, amountUzs: next, autoAmount: Boolean(listPrice) }];
    });
  }, [listPrice]);

  const effectivePrice = listPrice || 0;
  const paymentsTotal = payments.reduce(
    (total, p) => total + (Number(p.amountUzs) || 0),
    0,
  );
  const remaining = effectivePrice - paymentsTotal;

  // With a promocode the discount is applied server-side, so the price shown
  // here is not the amount due and the balance cannot be judged from it. The
  // prepare endpoint does that check and returns a precise message.
  const hasPromo = Boolean(sale.promocode.trim());

  // A "Sale" must be paid in full; Credit and Prepayment may be partial.
  const balanceState = hasPromo
    ? "is-settled"
    : remaining < 0
      ? "is-over"
      : remaining === 0 && paymentsTotal > 0
        ? "is-settled"
        : sale.saleType === "Sale"
          ? "is-short"
          : "is-settled";

  // A card taken on another terminal is only traceable by that terminal's
  // reference, and the fiscal terminal does not check the money arrived.
  const incompleteOtherTerminal = payments.some(
    (p) =>
      p.paymentType === "CardOther" &&
      Number(p.amountUzs) > 0 &&
      (!p.cardBrand || !String(p.externalReference || "").trim()),
  );
  const hasOtherTerminal = payments.some((p) => p.paymentType === "CardOther");
  const missingMethod = payments.some(
    (p) => Number(p.amountUzs) > 0 && !p.paymentType,
  );

  const blockedReason = !connected
    ? "The terminal is not connected"
    : !hasShift
      ? "Open a shift first"
      : !sale.exam || !sale.module
        ? "Choose an exam and module"
        : !effectivePrice
          ? "This module has no price on the price list"
          : paymentsTotal <= 0
            ? "Enter the payment"
            : missingMethod
              ? "Choose how the candidate is paying"
              : incompleteOtherTerminal
              ? "Choose the card type and enter the receipt reference from the other terminal"
              : hasPromo
                ? null
                : remaining < 0
                  ? `Payments exceed the price by ${moneyUzs(-remaining)}`
                  : sale.saleType === "Sale" && remaining > 0
                    ? `${moneyUzs(remaining)} still to allocate`
                    : null;

  // ---------------------------------------------------------------- helpers

  const runCommand = async ({ command, data, id, timeoutMs }) => {
    const requestId =
      id ||
      `${command.toLowerCase()}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
    const result = await sendCommand({
      id: requestId,
      command,
      data,
      timeoutMs,
    });
    return { requestId, response: result.response };
  };

  const reportTerminal = (response, successText) => {
    if (response.status === "Success") {
      // Success can still carry a warning, most often a printer problem.
      if (response.error) {
        toast.warn(
          `${successText}, but: ${explainTerminalError(response.error)}`,
        );
      } else {
        toast.success(successText);
      }
      return true;
    }
    toast.error(explainTerminalError(response.error));
    return false;
  };

  const handleBridgeError = (error) => {
    if (error instanceof BridgeError) {
      toast.error(error.message);
      if (error.retryableWithSameId) {
        toast.info(
          "The terminal may still have processed this. Check the unfinished sales list before trying again.",
          { autoClose: 8000 },
        );
      }
    } else {
      toast.error(error.message || "Something went wrong");
    }
  };

  // ---------------------------------------------------------------- actions

  const simpleCommand = async (command, successText) => {
    setBusy(true);
    try {
      const { requestId, response } = await runCommand({ command });
      reportTerminal(response, successText);
      await axios
        .post("/api/pos/command-log", { command, requestId, response })
        .catch(() => {});
      if (command === "Logout" && response.status === "Success") {
        setOpenShiftUser("");
        sessionStorage.removeItem("posShiftUser");
      }
    } catch (error) {
      handleBridgeError(error);
    } finally {
      setBusy(false);
      refreshStatus();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!shift.login.trim() || !shift.password.trim()) {
      return toast.error("Enter the cashier's name and terminal PIN");
    }
    setBusy(true);
    try {
      const { requestId, response } = await runCommand({
        command: "Login",
        data: { login: shift.login.trim(), password: shift.password },
      });
      if (reportTerminal(response, "Shift opened")) {
        setOpenShiftUser(shift.login.trim());
        sessionStorage.setItem("posShiftUser", shift.login.trim());
        setShift({ ...shift, password: "" });
      }
      await axios
        .post("/api/pos/command-log", { command: "Login", requestId, response })
        .catch(() => {});
    } catch (error) {
      handleBridgeError(error);
    } finally {
      setBusy(false);
    }
  };

  const openSettings = () => {
    setSettings(getBridgeSettings());
    setSettingsScope(
      isAdmin && bridgeInfo?.effective?.source === "system" ? "system" : "user",
    );
    setSettingsOpen(true);
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const data = await storeBridgeSettings({
        ...settings,
        scope: settingsScope,
      });
      if (data) setBridgeInfo(data);
      setSettings(getBridgeSettings());
      setSettingsOpen(false);
      toast.success(
        settingsScope === "system"
          ? "Saved as the default for every POS user"
          : "Saved to your account",
      );
      refreshStatus();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Could not save the POS settings",
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const handleUseSharedSettings = async () => {
    try {
      const data = await clearStoredBridgeSettings("user");
      if (data) setBridgeInfo(data);
      setSettings(getBridgeSettings());
      toast.success("Using the default settings for every POS user");
      refreshStatus();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not update settings");
    }
  };

  const handleSetAddress = async () => {
    try {
      await setTerminalAddress(terminalAddress.host, terminalAddress.port);
      toast.success("Terminal address updated");
      refreshStatus();
    } catch (error) {
      handleBridgeError(error);
    }
  };

  /**
   * Prepare on the server, send through the bridge, then post the result back.
   * The request id comes from the server and is reused on any retry, so the
   * terminal replays its stored answer instead of registering a second sale.
   */
  const loadPromocodeRequests = async () => {
    try {
      const { data } = await axios.get("/api/pos/promocode-requests", {
        params: { limit: 10 },
      });
      setPromoRequests(data?.items || []);
    } catch {
      /* the list is a convenience - never block the till on it */
    }
  };

  /**
   * Asks for a discount code for whoever is on screen. Everything but the note
   * and the discount is taken from the sale form, so nothing is typed twice.
   */
  const requestPromocode = async () => {
    const note = promoNote.trim();
    const value = Number(promoDiscountValue);

    if (!sale.exam || !sale.module) {
      toast.error("Choose an exam and module first");
      return;
    }
    if (!sale.firstName.trim() || !sale.lastName.trim()) {
      toast.error("Fill in the candidate's first and last name");
      return;
    }
    if (!sale.pinpp.trim() && !sale.passportId.trim()) {
      toast.error("Fill in the PINFL or the passport number");
      return;
    }
    if (note.length < 3) {
      toast.error(
        "Write a note explaining why this discount is being asked for",
      );
      return;
    }
    if (!Number.isFinite(value) || value <= 0) {
      toast.error("Enter the discount");
      return;
    }
    if (promoDiscountType === "percentage" && value > 100) {
      toast.error("A percentage discount cannot exceed 100");
      return;
    }

    setPromoRequesting(true);
    try {
      const { data } = await axios.post("/api/pos/promocode-request", {
        exam: sale.exam,
        module: sale.module,
        examDate: sale.examDate,
        firstName: sale.firstName,
        lastName: sale.lastName,
        pinpp: sale.pinpp,
        passportId: sale.passportId,
        phoneNumber: sale.phoneNumber,
        email: sale.email,
        discountType: promoDiscountType,
        discountValue: value,
        note,
      });
      setPromoResult(data);
      setPromoNote("");
      setPromoDiscountValue("");
      if (data?.warning) toast.warn(data.warning);
      else toast.success(`Requested ${data.code}`);
      await loadPromocodeRequests();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not request a promocode",
      );
    } finally {
      setPromoRequesting(false);
    }
  };

  // After a registered sale the till starts the next one from scratch: nothing
  // from the previous candidate (exam, details, payments, discount request)
  // carries over. The result panel stays so its receipt can still be reprinted.
  const resetSaleForm = () => {
    setSale(emptySale);
    setPayments([newPayment()]);
    setPromoNote("");
    setPromoDiscountType("percentage");
    setPromoDiscountValue("");
    setPromoResult(null);
    setDiscountOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSale = async (e) => {
    e.preventDefault();
    if (blockedReason) return toast.error(blockedReason);

    setBusy(true);
    setLastResult(null);
    let prepared;
    try {
      const { data } = await axios.post("/api/pos/sale/prepare", {
        ...sale,
        payments: payments
          .filter((p) => Number(p.amountUzs) > 0)
          .map(toTerminalTender),
        terminalUser: openShiftUser,
      });
      prepared = data;
    } catch (error) {
      setBusy(false);
      return toast.error(
        error.response?.data?.message || "Could not prepare this sale",
      );
    }

    try {
      const result = await sendCommand({
        id: prepared.requestId,
        command: "Sale",
        data: prepared.data,
      });
      const { data } = await axios.post(
        `/api/pos/sale/${prepared.posSaleId}/result`,
        { response: result.response },
      );
      await ackJournalEntry(prepared.requestId).catch(() => {});

      setLastResult({ posSale: data.posSale, response: result.response });
      if (reportTerminal(result.response, "Sale registered")) {
        resetSaleForm();
        // The sale is already booked; a printer problem is a nuisance, not a
        // failure, so it never rolls anything back.
        printStoredReceipt(data.posSale, {
          silent: true,
          copies: RECEIPT_COPIES,
        });
      }
    } catch (error) {
      handleBridgeError(error);
    } finally {
      setBusy(false);
      loadUnresolved();
      refreshStatus();
    }
  };

  /** Re-sends a sale whose outcome we never recorded, with its original id. */
  const resolvePending = async (posSale) => {
    setBusy(true);
    try {
      const result = await sendCommand({
        id: posSale.requestId,
        command: "Sale",
        data: posSale.request,
      });
      await axios.post(`/api/pos/sale/${posSale._id}/result`, {
        response: result.response,
      });
      await ackJournalEntry(posSale.requestId).catch(() => {});
      reportTerminal(result.response, "Outcome recorded");
    } catch (error) {
      handleBridgeError(error);
    } finally {
      setBusy(false);
      loadUnresolved();
    }
  };

  /** Fills the candidate's name from their PINFL, as the payment form does. */
  const lookupPinpp = async () => {
    const pinpp = String(sale.pinpp ?? "").trim();
    if (!/^\d{14}$/.test(pinpp)) {
      return toast.error("Enter a valid 14-digit PINFL");
    }

    const secret = process.env.REACT_APP_PIN_INFO_SECRET;
    if (!secret) {
      return toast.error(
        "Missing PIN info secret. Set REACT_APP_PIN_INFO_SECRET and rebuild.",
      );
    }

    setLookingUp(true);
    try {
      const { data } = await axios.post(
        "/api/pin-info",
        { pinpp },
        { headers: { "x-pin-info-secret": secret } },
      );
      setSale((prev) => ({
        ...prev,
        pinpp: data?.pinpp ?? pinpp,
        firstName: data?.firstName || prev.firstName,
        lastName: data?.lastName || prev.lastName,
      }));
      toast.success("Details found");
    } catch (error) {
      const status = error?.response?.status;
      toast.error(
        status === 400
          ? "Invalid PINFL. Please check and try again."
          : status === 401
            ? "PIN info secret is missing or invalid."
            : status === 502
              ? "PINFL lookup failed upstream. Try again shortly."
              : "Could not look up this PINFL.",
      );
    } finally {
      setLookingUp(false);
    }
  };

  /** Renders the stored electronic receipt and sends it to the printer. */
  const printStoredReceipt = async (
    posSale,
    { silent = false, copies = 1 } = {},
  ) => {
    const receipt = posSale?.receipt;
    if (!receipt) {
      if (!silent) toast.error("This sale has no stored receipt to print");
      return;
    }

    setPrinting(true);
    try {
      // Rendered once, sent as many times as asked for: each job ends with its
      // own cut, so the copies come off the printer as separate receipts.
      const payload = await buildPrintPayload(receipt);
      for (let copy = 0; copy < Math.max(1, copies); copy += 1) {
        // eslint-disable-next-line no-await-in-loop
        await printReceipt(payload);
      }
      if (!silent) {
        toast.success(
          copies > 1
            ? `${copies} receipts sent to the printer`
            : "Receipt sent to the printer",
        );
      }
      (posSale.paymentIds || []).forEach((paymentId) =>
        axios
          .post(`/api/payments/${paymentId}/receipt-event`, {
            event: "printed",
            source: "pos",
          })
          .catch(() => {}),
      );
    } catch (error) {
      // Name the printer explicitly - a cashier should not have to guess
      // whether the sale failed or only the paper did.
      toast.warn(
        `Sale is registered, but the receipt did not print: ${error.message}`,
        { autoClose: 8000 },
      );
    } finally {
      setPrinting(false);
    }
  };

  const setPayment = (index, patch) => {
    const next = [...payments];
    next[index] = { ...next[index], ...patch };
    setPayments(next);
  };

  const methodLabel = (value) =>
    PAYMENT_TYPE_OPTIONS.find((option) => option.value === value)?.label ||
    value;

  const saleSucceeded = lastResult?.response?.status === "Success";

  // ----------------------------------------------------------------- render

  const renderShiftLogin = () => (
    <Card className="mb-3 pos-shift-card">
      <CardBody>
        <Step title="Open a shift to start selling" />
        {!connected && (
          <FormText className="d-block mb-3">
            Connect the terminal first. Its status is at the top of the page.
          </FormText>
        )}
        <Form onSubmit={handleLogin}>
          <FormGroup>
            <Label for="pos-cashier">Cashier</Label>
            <Input
              id="pos-cashier"
              autoComplete="off"
              value={shift.login}
              onChange={(e) => setShift({ ...shift, login: e.target.value })}
              placeholder="First Last"
            />
            <FormText>
              Exactly as registered on the terminal, first and last name
              separated by a space.
            </FormText>
          </FormGroup>
          <FormGroup>
            <Label for="pos-pin">Terminal PIN</Label>
            <Input
              id="pos-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={shift.password}
              onChange={(e) => setShift({ ...shift, password: e.target.value })}
            />
          </FormGroup>
          <Button
            color="primary"
            block
            type="submit"
            disabled={busy || !connected}
          >
            {busy ? "Opening shift…" : "Open shift"}
          </Button>
        </Form>
      </CardBody>
    </Card>
  );

  const renderPaymentRow = (payment, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <div className="pos-tender" key={index}>
      <div className="pos-tender-head">
        <Label for={`pos-amount-${index}`} className="mb-0">
          {payments.length > 1 ? `Payment ${index + 1}` : "Amount"}
        </Label>
        {payments.length > 1 && (
          <button
            type="button"
            className="pos-icon-action"
            aria-label={`Remove payment ${index + 1}`}
            title="Remove this payment"
            onClick={() =>
              setPayments(payments.filter((_, i) => i !== index))
            }
          >
            <i className="simple-icon-close" aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="pos-amount">
        <Input
          id={`pos-amount-${index}`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={payment.amountUzs}
          placeholder="0"
          onChange={(e) =>
            setPayment(index, {
              amountUzs: e.target.value.replace(/\D/g, ""),
              autoAmount: false,
            })
          }
        />
        <span className="pos-amount-unit">UZS</span>
      </div>
      {payment.amountUzs && (
        <div className="pos-amount-readout">{moneyUzs(payment.amountUzs)}</div>
      )}

      <div
        className={`pos-methods ${
          Number(payment.amountUzs) > 0 && !payment.paymentType
            ? "is-missing"
            : ""
        }`}
        role="radiogroup"
        aria-label={
          payments.length > 1
            ? `How payment ${index + 1} is made`
            : "How the candidate is paying"
        }
      >
        {PAYMENT_TYPE_OPTIONS.map((option) => {
          const selected = payment.paymentType === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`pos-method ${selected ? "is-selected" : ""}`}
              onClick={() =>
                setPayment(index, { paymentType: option.value })
              }
            >
              <i className={METHOD_ICONS[option.value]} aria-hidden="true" />
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>

      {payment.paymentType === "CardOther" && (
        <div className="pos-tender-detail">
          <SelectInput
            aria-label="Card type"
            value={payment.cardBrand || ""}
            onChange={(cardBrand) => setPayment(index, { cardBrand })}
            options={OTHER_TERMINAL_CARDS}
            placeholder="Card type"
          />
          <Input
            aria-label="Receipt number or RRN from the other terminal"
            value={payment.externalReference || ""}
            placeholder="Receipt no. or RRN from that terminal"
            onChange={(e) =>
              setPayment(index, { externalReference: e.target.value })
            }
          />
        </div>
      )}
    </div>
  );

  const renderLastResult = () =>
    lastResult && (
      <div className={`pos-receipt mb-3 ${saleSucceeded ? "" : "is-fail"}`}>
        <div className="headline" role="status">
          {saleSucceeded
            ? "Sale registered"
            : explainTerminalError(lastResult.response.error)}
        </div>
        {saleSucceeded && (
          <dl>
            <dt>Sale ID</dt>
            <dd>{lastResult.posSale.saleId}</dd>
            <dt>Total</dt>
            <dd>{money(lastResult.posSale.totalAmount)}</dd>
            {lastResult.posSale.saleReceipt?.fSign && (
              <>
                <dt>Fiscal sign</dt>
                <dd>{lastResult.posSale.saleReceipt.fSign}</dd>
              </>
            )}
          </dl>
        )}
        <div className="d-flex flex-wrap align-items-center mt-2">
          {saleSucceeded && (
            <Button
              size="xs"
              color="light"
              className="mr-2"
              disabled={printing}
              onClick={() => printStoredReceipt(lastResult.posSale)}
            >
              {printing ? "Printing…" : "Print receipt again"}
            </Button>
          )}
          {lastResult.posSale?.saleReceipt?.qr && (
            <a
              className="text-small"
              href={lastResult.posSale.saleReceipt.qr}
              target="_blank"
              rel="noopener noreferrer"
            >
              Fiscal receipt on ofd.soliq.uz
            </a>
          )}
          <button
            type="button"
            className="pos-icon-action ml-auto"
            aria-label="Dismiss"
            title="Dismiss"
            onClick={() => setLastResult(null)}
          >
            <i className="simple-icon-close" aria-hidden="true" />
          </button>
        </div>
      </div>
    );

  return (
    <AppLayout noFooter>
      <div className="dashboard-wrapper pos-page">
        {/* -------------------------------------------- status header -- */}
        <Card className="m-4 mb-3">
          <CardBody className="pos-header">
            <div className="d-flex flex-wrap justify-content-between align-items-center">
              <div className="d-flex flex-wrap align-items-center mr-3">
                <h1 className="pos-title">POS Terminal</h1>
                <div className="pos-statusbar">
                  <Pill
                    state={statusError ? "off" : "ok"}
                    label="Bridge"
                    value={statusError ? "unavailable" : "running"}
                  />
                  <Pill
                    state={connected ? "ok" : statusError ? "off" : "warn"}
                    label="Terminal"
                    value={
                      connected
                        ? status.terminal.address
                        : statusError
                          ? "unknown"
                          : "disconnected"
                    }
                  />
                  <Pill
                    state={hasShift ? "ok" : "warn"}
                    label="Shift"
                    value={hasShift ? openShiftUser : "closed"}
                  />
                  {status?.pendingSync > 0 && (
                    <Pill
                      state="warn"
                      label="Waiting to sync"
                      value={status.pendingSync}
                    />
                  )}
                </div>
              </div>
              <div className="pos-header-links">
                <NavLink to="/app/pos/sales" className="btn btn-light btn-sm">
                  Sales
                </NavLink>
                {canManagePrices && (
                  <NavLink to="/app/prices" className="btn btn-light btn-sm">
                    Prices
                  </NavLink>
                )}
                <Button color="light" size="sm" onClick={openSettings}>
                  Settings
                </Button>
              </div>
            </div>

            {statusError && (
              <Alert color="danger" className="mt-3 mb-0">
                <strong>{statusError}</strong>
                <div className="text-small mt-1">
                  The POS bridge runs on this computer and talks to the terminal
                  over the office network. Start it, then check the address and
                  token under Settings.
                </div>
              </Alert>
            )}

            {!connected && !statusError && (
              <Alert color="warning" className="mt-3 mb-0">
                <strong>The bridge is not connected to a terminal.</strong>
                {status?.terminal?.lastError && (
                  <div className="text-small">
                    Last error: {status.terminal.lastError}
                  </div>
                )}
                <Row className="mt-2 align-items-end">
                  <Col xs="5" sm="4" md="3">
                    <Label for="pos-host" className="text-small mb-1">
                      Terminal host
                    </Label>
                    <Input
                      id="pos-host"
                      bsSize="sm"
                      value={terminalAddress.host}
                      onChange={(e) =>
                        setAddress({ ...terminalAddress, host: e.target.value })
                      }
                      placeholder="192.168.1.50"
                    />
                  </Col>
                  <Col xs="4" sm="3" md="2">
                    <Label for="pos-port" className="text-small mb-1">
                      Port
                    </Label>
                    <Input
                      id="pos-port"
                      bsSize="sm"
                      inputMode="numeric"
                      value={terminalAddress.port}
                      onChange={(e) =>
                        setAddress({ ...terminalAddress, port: e.target.value })
                      }
                      placeholder="8080"
                    />
                  </Col>
                  <Col xs="3" sm="5" md="7" className="pt-2">
                    <Button
                      size="sm"
                      color="primary"
                      onClick={handleSetAddress}
                    >
                      Connect
                    </Button>
                    <Button
                      size="sm"
                      color="light"
                      className="ml-2"
                      onClick={() => reconnectTerminal().then(refreshStatus)}
                    >
                      Retry
                    </Button>
                  </Col>
                </Row>
                <div className="text-small mt-2">
                  The terminal shows its address at the bottom of the screen in
                  ERP mode.
                </div>
              </Alert>
            )}

            {unresolved.length > 0 && (
              <Alert color="warning" className="mt-3 mb-0">
                <strong>
                  {unresolved.length} sale
                  {unresolved.length === 1 ? "" : "s"} with no recorded outcome
                </strong>
                <div className="text-small mb-2">
                  These reached the terminal but we never learned what happened.
                  Asking again with the same request id is safe: the terminal
                  replays its stored answer rather than charging twice.
                </div>
                {unresolved.map((item) => (
                  <div key={item._id} className="pos-unresolved">
                    <span className="who">
                      {item.candidate?.fullName || "Unnamed candidate"}
                    </span>
                    <span className="text-small text-muted">
                      {item.exam} {item.module}, {money(item.totalAmount)}
                    </span>
                    <Button
                      size="xs"
                      color="primary"
                      className="ml-auto"
                      disabled={busy || !connected}
                      onClick={() => resolvePending(item)}
                    >
                      Ask the terminal
                    </Button>
                  </div>
                ))}
              </Alert>
            )}
          </CardBody>
        </Card>

        {/* No page-wide form: the shift login is its own <form>, and a form
            nested in another one is submitted natively by Chrome without
            React's onSubmit running, which reloaded the page. Only the
            checkout card below is the sale form. */}
        <Row className="mx-2">
          {/* ------------------------------------- what + who (left) -- */}
          <Col lg="7" xl="8" className="mb-4">
            <Card className="mb-3">
              <CardBody>
                <Step n="1" title="What is being paid for" />
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="pos-exam">Exam</Label>
                      <SelectInput
                        inputId="pos-exam"
                        value={sale.exam}
                        onChange={(exam) =>
                          setSale({ ...sale, exam, module: "" })
                        }
                        options={examOptions}
                        placeholder="Choose an exam"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="pos-module">Module</Label>
                      <SelectInput
                        inputId="pos-module"
                        value={sale.module}
                        onChange={(module) => setSale({ ...sale, module })}
                        options={moduleOptions}
                        isDisabled={!sale.exam}
                        placeholder={
                          sale.exam ? "Choose a module" : "Pick an exam first"
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {sale.module && !listPrice && (
                  <Alert color="warning" className="mb-0">
                    This module has no price on the price list, so it can't
                    be sold yet.
                    {canManagePrices && (
                      <>
                        {" "}
                        <NavLink to="/app/prices">Add a price</NavLink>
                      </>
                    )}
                  </Alert>
                )}
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardBody>
                <Step n="2" title="Who is paying" hint="Optional" />
                <Row>
                  <Col md="8">
                    <FormGroup>
                      <Label for="pos-pinpp">PINFL</Label>
                      <div className="d-flex">
                        <Input
                          id="pos-pinpp"
                          inputMode="numeric"
                          autoComplete="off"
                          value={sale.pinpp}
                          placeholder="14 digits"
                          maxLength={14}
                          onChange={(e) =>
                            setSale({
                              ...sale,
                              pinpp: e.target.value.replace(/\D/g, ""),
                            })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              lookupPinpp();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          color="light"
                          className="ml-2 text-nowrap"
                          disabled={lookingUp || sale.pinpp.length !== 14}
                          onClick={lookupPinpp}
                        >
                          {lookingUp ? "Looking up…" : "Get details"}
                        </Button>
                      </div>
                      <FormText>
                        Fills the name from the citizen register. Press Enter
                        in the field to look it up.
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="pos-passport">Passport</Label>
                      <Input
                        id="pos-passport"
                        autoComplete="off"
                        spellCheck={false}
                        value={sale.passportId}
                        onChange={(e) =>
                          setSale({
                            ...sale,
                            passportId: e.target.value.toUpperCase(),
                          })
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="pos-first-name">First name</Label>
                      <Input
                        id="pos-first-name"
                        autoComplete="off"
                        value={sale.firstName}
                        onChange={(e) =>
                          setSale({ ...sale, firstName: e.target.value })
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="pos-last-name">Last name</Label>
                      <Input
                        id="pos-last-name"
                        autoComplete="off"
                        value={sale.lastName}
                        onChange={(e) =>
                          setSale({ ...sale, lastName: e.target.value })
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className="mb-md-0">
                      <Label for="pos-enquiry">Enquiry ID</Label>
                      <Input
                        id="pos-enquiry"
                        autoComplete="off"
                        value={sale.enquiryId}
                        onChange={(e) =>
                          setSale({ ...sale, enquiryId: e.target.value })
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className="mb-0">
                      <Label for="pos-exam-date">Exam date</Label>
                      <DatePicker
                        id="pos-exam-date"
                        className="form-control"
                        autoComplete="off"
                        placeholderText="DD/MM/YYYY"
                        dateFormat="dd/MM/yyyy"
                        // an exam cannot have already happened
                        minDate={new Date()}
                        selected={
                          sale.examDate &&
                          moment(sale.examDate, "DD/MM/YYYY", true).isValid()
                            ? moment(sale.examDate, "DD/MM/YYYY").toDate()
                            : null
                        }
                        onChange={(date) =>
                          setSale({
                            ...sale,
                            examDate: date
                              ? moment(date).format("DD/MM/YYYY")
                              : "",
                          })
                        }
                        // type the digits and the slashes appear
                        onChangeRaw={(e) => {
                          if (e.target.value === undefined) return;
                          const masked = maskExamDate(e.target.value);
                          e.target.value = masked;
                          setSale((prev) => ({ ...prev, examDate: masked }));
                        }}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* --------------------------------- request a promocode -- */}
            <Card>
              <CardBody>
                <button
                  type="button"
                  className="pos-collapse-toggle"
                  aria-expanded={discountOpen}
                  onClick={() => setDiscountOpen(!discountOpen)}
                >
                  <Step
                    title="Request a discount code"
                    hint="Approved in Telegram"
                  />
                  <i
                    className={
                      discountOpen
                        ? "simple-icon-arrow-up"
                        : "simple-icon-arrow-down"
                    }
                    aria-hidden="true"
                  />
                </button>
                <Collapse isOpen={discountOpen}>
                  <FormText className="d-block mt-3 mb-3">
                    Uses the candidate and exam filled in above. The code is
                    created straight away so you can read it out, but it only
                    works once management approves it.
                  </FormText>
                  <Row>
                    <Col md="3">
                      <FormGroup>
                        <Label for="pos-discount-type">Discount</Label>
                        <SelectInput
                          inputId="pos-discount-type"
                          value={promoDiscountType}
                          onChange={(value) => setPromoDiscountType(value)}
                          options={[
                            { label: "Percentage (%)", value: "percentage" },
                            { label: "Amount (UZS)", value: "amount" },
                          ]}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="pos-discount-value">
                          {promoDiscountType === "percentage"
                            ? "Percent off"
                            : "Amount off"}
                        </Label>
                        <Input
                          id="pos-discount-value"
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          value={promoDiscountValue}
                          onChange={(e) =>
                            setPromoDiscountValue(
                              e.target.value.replace(/[^\d.]/g, ""),
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="pos-discount-note">Reason</Label>
                        <Input
                          id="pos-discount-note"
                          type="text"
                          value={promoNote}
                          placeholder="Why this candidate should get a discount"
                          onChange={(e) => setPromoNote(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Button
                    type="button"
                    color="primary"
                    disabled={promoRequesting}
                    onClick={requestPromocode}
                  >
                    {promoRequesting ? "Requesting…" : "Request code"}
                  </Button>
                  <Button
                    type="button"
                    color="light"
                    className="ml-2"
                    onClick={loadPromocodeRequests}
                  >
                    Show recent requests
                  </Button>

                  {promoResult?.code && (
                    <Alert color="info" className="mt-3 mb-0">
                      <strong>{promoResult.code}</strong> requested and
                      waiting for approval. It won't discount anything until
                      then.
                    </Alert>
                  )}

                  {promoRequests.length > 0 && (
                    <dl className="pos-kv mt-3 mb-0">
                      {promoRequests.map((one) => (
                        <React.Fragment key={one._id}>
                          <dt>{one.code}</dt>
                          <dd>
                            <PromoRequestBadge status={one.status} />{" "}
                            {one.exam} {one.module},{" "}
                            {one.discountType === "percentage"
                              ? `${one.discountValue}%`
                              : moneyUzs(one.discountValue)}
                          </dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  )}
                </Collapse>
              </CardBody>
            </Card>
          </Col>

          {/* --------------------------------- checkout panel (right) -- */}
          <Col lg="5" xl="4" className="mb-4">
            <div className="pos-sticky">
              {!hasShift && renderShiftLogin()}

              <Card className="mb-3 pos-checkout">
                <CardBody tag={Form} onSubmit={handleSale}>
                  {renderLastResult()}

                  <Step
                    n="3"
                    title="Payment"
                    hint={
                      payments.length > 1
                        ? `${payments.length} payments`
                        : undefined
                    }
                  />

                  <div className="pos-due">
                    <span className="k">Price</span>
                    <span className="v">
                      {listPrice ? moneyUzs(listPrice) : "—"}
                    </span>
                  </div>

                  {payments.map(renderPaymentRow)}

                  {hasOtherTerminal && (
                    <div className="pos-tender-hint">
                      Take the payment on the bank terminal first. This
                      terminal records it for the tax service but doesn't
                      check that the money arrived.
                    </div>
                  )}

                  <div className="d-flex flex-wrap mb-3">
                    <Button
                      size="xs"
                      color="light"
                      type="button"
                      className="mr-2 mb-1"
                      onClick={() => setPayments([...payments, newPayment()])}
                    >
                      <i className="simple-icon-plus mr-1" aria-hidden="true" />
                      Split payment
                    </Button>
                    {remaining > 0 && !hasPromo && payments.length > 1 && (
                      <Button
                        size="xs"
                        color="light"
                        type="button"
                        className="mb-1"
                        onClick={() =>
                          setPayment(payments.length - 1, {
                            amountUzs: String(
                              (Number(
                                payments[payments.length - 1].amountUzs,
                              ) || 0) + remaining,
                            ),
                            autoAmount: false,
                          })
                        }
                      >
                        Fill remaining ({moneyUzs(remaining)})
                      </Button>
                    )}
                  </div>

                  <FormGroup>
                    <Label for="pos-sale-type">Sale type</Label>
                    <SelectInput
                      inputId="pos-sale-type"
                      value={sale.saleType}
                      onChange={(saleType) => setSale({ ...sale, saleType })}
                      options={SALE_TYPE_OPTIONS}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="pos-promocode">Promocode</Label>
                    <Input
                      id="pos-promocode"
                      autoComplete="off"
                      spellCheck={false}
                      value={sale.promocode}
                      placeholder="Optional"
                      onChange={(e) =>
                        setSale({
                          ...sale,
                          promocode: e.target.value.toUpperCase(),
                        })
                      }
                    />
                  </FormGroup>

                  <div className="pos-summary">
                    <div className="line">
                      <span className="k">Price</span>
                      <span className="v">{moneyUzs(effectivePrice)}</span>
                    </div>
                    {hasPromo && (
                      <div className="line">
                        <span className="k">Promocode</span>
                        <span className="v">{sale.promocode}</span>
                      </div>
                    )}
                    {payments.length > 1 &&
                      payments
                        .filter((p) => Number(p.amountUzs) > 0)
                        .map((p, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <div className="line" key={i}>
                            <span className="k">
                              {p.paymentType
                                ? methodLabel(p.paymentType)
                                : "Method not chosen"}
                            </span>
                            <span className="v">{moneyUzs(p.amountUzs)}</span>
                          </div>
                        ))}
                    <div className="line total">
                      <span className="k">Taking now</span>
                      <span className="v">{moneyUzs(paymentsTotal)}</span>
                    </div>
                    {effectivePrice > 0 && (
                      <div className={`balance ${balanceState}`}>
                        {hasPromo
                          ? "The discount is applied when the sale is prepared, so the amount due is checked then"
                          : remaining === 0
                            ? "Payments match the price"
                            : remaining > 0
                              ? sale.saleType === "Sale"
                                ? `${moneyUzs(remaining)} still to allocate`
                                : `${moneyUzs(remaining)} outstanding, allowed for ${sale.saleType}`
                              : `Payments exceed the price by ${moneyUzs(-remaining)}`}
                      </div>
                    )}
                  </div>

                  <FormGroup check className="mt-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={sale.shouldPrintReceipt}
                        onChange={(e) =>
                          setSale({
                            ...sale,
                            shouldPrintReceipt: e.target.checked,
                          })
                        }
                      />{" "}
                      Also print the receipt on the terminal
                    </Label>
                  </FormGroup>

                  <Button
                    color="primary"
                    size="lg"
                    block
                    type="submit"
                    className="mt-3 pos-register"
                    disabled={busy || Boolean(blockedReason)}
                    aria-describedby={
                      blockedReason ? "pos-blocked-reason" : undefined
                    }
                  >
                    {busy
                      ? "Registering…"
                      : paymentsTotal > 0
                        ? `Register sale, ${moneyUzs(paymentsTotal)}`
                        : "Register sale"}
                  </Button>
                  {blockedReason && (
                    <p
                      id="pos-blocked-reason"
                      className="pos-blocked text-small mt-2 mb-0"
                      role="status"
                    >
                      {blockedReason}
                    </p>
                  )}
                </CardBody>
              </Card>

              {/* ------------------------------- shift + reports -- */}
              <Card>
                <CardBody>
                  {hasShift && (
                    <div className="pos-shift-line">
                      <span>
                        Shift open for <strong>{openShiftUser}</strong>
                      </span>
                      <Button
                        color="light"
                        size="xs"
                        disabled={busy || !connected}
                        onClick={() =>
                          simpleCommand("Logout", "Shift closed")
                        }
                      >
                        Close shift
                      </Button>
                    </div>
                  )}
                  <button
                    type="button"
                    className="pos-collapse-toggle"
                    aria-expanded={reportsOpen}
                    onClick={() => setReportsOpen(!reportsOpen)}
                  >
                    <Step title="Reports and paper" />
                    <i
                      className={
                        reportsOpen
                          ? "simple-icon-arrow-up"
                          : "simple-icon-arrow-down"
                      }
                      aria-hidden="true"
                    />
                  </button>
                  <Collapse isOpen={reportsOpen}>
                    <div className="pos-actions mt-3">
                      <Button
                        color="light"
                        disabled={busy || !connected}
                        onClick={() => setConfirmCommand("PrintXReport")}
                      >
                        X-report
                      </Button>
                      <Button
                        color="light"
                        disabled={busy || !connected}
                        onClick={() => setConfirmCommand("PrintLastReceipt")}
                      >
                        Reprint receipt
                      </Button>
                      <Button
                        color="danger"
                        outline
                        className="span-2"
                        disabled={busy || !connected}
                        onClick={() => setConfirmCommand("CloseZReport")}
                      >
                        Close Z-report
                      </Button>
                    </div>
                    <FormText className="mt-2">
                      Closing the Z-report ends the fiscal day. Closing a
                      shift doesn't; it starts settlement in UzcardPos.
                    </FormText>
                  </Collapse>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>

        <Modal isOpen={settingsOpen} toggle={() => setSettingsOpen(false)}>
          <ModalHeader>POS bridge settings</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Bridge address</Label>
              <Input
                value={settings.url}
                onChange={(e) =>
                  setSettings({ ...settings, url: e.target.value })
                }
              />
              <FormText>
                Where the bridge runs on this computer. Default
                http://localhost:9123
              </FormText>
            </FormGroup>
            <FormGroup>
              <Label>Bridge token</Label>
              <Input
                type="password"
                value={settings.token}
                onChange={(e) =>
                  setSettings({ ...settings, token: e.target.value })
                }
              />
              <FormText>
                The BRIDGE_TOKEN value from the bridge's .env file.
              </FormText>
            </FormGroup>
            {isAdmin && (
              <FormGroup>
                <Label>Save for</Label>
                <SelectInput
                  value={settingsScope}
                  onChange={setSettingsScope}
                  options={SETTINGS_SCOPES}
                />
                <FormText>
                  Every POS user gets the default unless their own account has
                  settings of its own.
                </FormText>
              </FormGroup>
            )}
            <FormText>
              Saved on the server, so the till reconnects by itself after
              signing out or restarting this computer.
            </FormText>
            {bridgeInfo?.effective && (
              <div className="text-small text-muted mt-2">
                In use:{" "}
                {bridgeInfo.effective.source === "account"
                  ? "your account's settings"
                  : "the default for every POS user"}
                {bridgeInfo.effective.updatedBy
                  ? `, saved by ${bridgeInfo.effective.updatedBy}`
                  : ""}
                {bridgeInfo.account && bridgeInfo.system && (
                  <Button
                    color="link"
                    size="xs"
                    className="p-0 ml-2 align-baseline"
                    onClick={handleUseSharedSettings}
                  >
                    Use the default instead
                  </Button>
                )}
              </div>
            )}
            <div className="pos-statusbar mt-3">
              <Pill
                state={statusError ? "off" : "ok"}
                label="Bridge"
                value={statusError ? "not reachable" : "reachable"}
              />
              <Pill
                state={connected ? "ok" : "warn"}
                label="Terminal"
                value={status?.terminal?.address || "not configured"}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setSettingsOpen(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSaveSettings}
              disabled={savingSettings}
            >
              {savingSettings ? "Saving…" : "Save"}
            </Button>
          </ModalFooter>
        </Modal>

        {confirmCommand && (
          <ConfirmModal
            modal={Boolean(confirmCommand)}
            setModal={(open) => {
              if (!open) setConfirmCommand(null);
            }}
            title={REPORT_COMMANDS[confirmCommand].title}
            body={REPORT_COMMANDS[confirmCommand].body}
            action={() =>
              simpleCommand(
                confirmCommand,
                REPORT_COMMANDS[confirmCommand].done,
              )
            }
            confirmLabel={REPORT_COMMANDS[confirmCommand].confirmLabel}
            confirmColor={
              REPORT_COMMANDS[confirmCommand].danger ? "danger" : "primary"
            }
          />
        )}
      </div>
    </AppLayout>
  );
};

export default PosTerminal;
